import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getContractDetails } from "~/features/contracts/api/getContractDetails";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import { POPUP_SET_INITIAL_VALUES } from "~/features/contracts/redux/actions";
import { pnaAvailabilityQueryOptions } from "~/domains/pna/pnaQueries";
import { toAvailabilityOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import Spinner from "~/common/components/Spinner/Spinner";
import { Stack } from "~/common/mui";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import NewContractDetailsSummary from "~/features/contracts/components/ContractDetails/ContractDetailsSummary";
import NewDeliveriesTable from "~/features/contracts/components/ContractDetails/DeliveriesTable";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { Alert } from "~/common/mui/Alert/Alert";

export interface ContractDetailsPageProps {
  contractNo: string;
  contractPosition?: number | null;
  materialNo: string;
  packageCode: string;
}

const ContractDetailsPage = ({ contractNo, contractPosition, materialNo, packageCode }: ContractDetailsPageProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [contractDetails, setContractDetails] = useState<ContractDetailsResponse>();
  const [hasError, setHasError] = useState(false);

  const productCode = contractDetails?.product.code;

  const availabilityOptions = pnaAvailabilityQueryOptions({ productCode: productCode ?? materialNo });
  const availabilityQuery = useQuery(availabilityOptions);
  const availabilityOutcome = toAvailabilityOutcomeFromQuery(availabilityQuery, availabilityOptions.enabled === true);
  const productAvailability = availabilityOutcome.status === "success" ? availabilityOutcome.data : undefined;

  useEffect(() => {
    let cancelled = false;

    getContractDetails(contractNo, materialNo, packageCode, contractPosition ?? null)
      .then((contractDetail) => {
        if (cancelled) {
          return;
        }

        dispatch(addBaseProductDataAction(contractDetail.product));
        setContractDetails(contractDetail);
        dispatch({
          type: POPUP_SET_INITIAL_VALUES,
          payload: { qty: contractDetail.nextDispositionQuantity, rdd: contractDetail.nextDisposition },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (hasError) {
    return (
      <Alert className="mt-3" severity="error">
        {t("generic.error.ajax")}
      </Alert>
    );
  }

  if (!contractDetails) {
    return <Spinner />;
  }

  return (
    <div>
      <PageTitle
        clta-details-page-element-marker="title"
        clta-contract-id={contractDetails.customerContractNumber}
      >{`${t("newContract.contractTile")} ${contractDetails.customerContractNumber}`}</PageTitle>

      <Stack gap={4}>
        <NewContractDetailsSummary contractDetails={contractDetails} productAvailability={productAvailability} />
        <NewDeliveriesTable contractLines={contractDetails?.contractLines} />
      </Stack>
    </div>
  );
};

export default ContractDetailsPage;

export function creatContractDetailsPage(props: ContractDetailsPageProps) {
  return <ContractDetailsPage {...props} />;
}
